import { cn } from '@listening/utils/react-helpers';
import IconSpeed from '@listening/assets/astro/icons/icon-speed.svg?react';
import * as m from '@/paraglide/messages';

import { Slider } from '@listening/frontend/components/radix/slider';
import { useState } from 'react';

const speedOptions: Array<string> = [
  '0.75',
  '1.0',
  '1.10',
  '1.15',
  '1.25',
  '1.5',
  '1.75',
  '1.85',
  '2.5',
];

export default function SpeedControlReact() {
  const defaultSpeedIndex = speedOptions.indexOf('1.25');
  const [selectedSpeed, setSelectedSpeed] = useState('1.25');
  const [sliderValue, setSliderValue] = useState([
    (defaultSpeedIndex / (speedOptions.length - 1)) * 100,
  ]);

  const handleSpeedClick = (speed: string): void => {
    setSelectedSpeed(speed);
    // Calculate slider position based on speed
    const index = speedOptions.indexOf(speed);
    const newSliderValue = [(index / (speedOptions.length - 1)) * 100];
    setSliderValue(newSliderValue);
  };

  const handleSliderChange = (value: Array<number>): void => {
    if (value[0] === undefined) {
      return;
    }
    // Calculate speed based on slider position
    const index = Math.round((value[0] / 100) * (speedOptions.length - 1));
    const newSpeed = speedOptions[index];
    if (newSpeed) {
      setSelectedSpeed(newSpeed);
      setSliderValue(value);
    }
  };

  return (
    <div
      aria-label='Playback speed control'
      className='flex-1 rounded-2xl bg-white/5 p-6'
      role='region'
    >
      <div className='mb-6 flex items-center gap-3'>
        <IconSpeed aria-hidden='true' />
        <span className='text-xl font-semibold'>
          {m.homepage_features_speed_label()}
        </span>
      </div>

      <div className='flex flex-col gap-4'>
        <div className='flex items-center gap-4'>
          <button
            aria-label='Decrease speed'
            className='flex size-10 items-center justify-center text-navy-220 hover:opacity-80'
            onClick={() => {
              const currentIndex = speedOptions.indexOf(selectedSpeed);
              if (currentIndex > 0) {
                const newSpeed = speedOptions[currentIndex - 1];
                if (newSpeed) {
                  handleSpeedClick(newSpeed);
                }
              }
            }}
            type='button'
          >
            <span aria-hidden='true' className='text-3xl font-medium'>
              −
            </span>
          </button>

          <div className='relative flex-1'>
            <Slider
              aria-label='Speed control slider'
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={sliderValue[0]}
              className='w-full'
              max={100}
              onValueChange={handleSliderChange}
              rangeClassName='bg-azure-595'
              rangeComponent={
                <div className='size-full cursor-pointer rounded-r-full bg-azure-595' />
              }
              step={1}
              thumbClassName='border-azure-595 bg-azure-595 cursor-pointer'
              trackClassName='bg-white/50 cursor-pointer'
              value={sliderValue}
            />
          </div>

          <button
            aria-label='Increase speed'
            className='flex size-10 items-center justify-center text-navy-220 hover:opacity-80'
            onClick={() => {
              const currentIndex = speedOptions.indexOf(selectedSpeed);
              if (currentIndex < speedOptions.length - 1) {
                const newSpeed = speedOptions[currentIndex + 1];
                if (newSpeed) {
                  handleSpeedClick(newSpeed);
                }
              }
            }}
            type='button'
          >
            <span aria-hidden='true' className='text-3xl font-medium'>
              +
            </span>
          </button>
        </div>

        <div
          aria-label='Speed presets'
          className='grid grid-cols-3 gap-3'
          role='radiogroup'
        >
          {speedOptions.map((speed) => (
            <button
              aria-label={`Set speed to ${speed}x`}
              aria-pressed={speed === selectedSpeed}
              className={cn(
                'rounded-lg px-4 py-3 text-base font-medium transition-colors lg:px-3 lg:py-2 lg:text-sm xl:px-6 xl:py-4 xl:text-lg',
                'text-navy-220',
                {
                  'bg-azure-525/15': speed === selectedSpeed,
                  'hover:bg-white/10': speed !== selectedSpeed,
                },
              )}
              key={speed}
              onClick={() => {
                handleSpeedClick(speed);
              }}
              type='button'
            >
              {speed}x
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
